import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

const PDFPreviewDialog = ({ open, onClose, fileId, tenantId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPDFPreview = async () => {
      if (!open || !fileId || !tenantId) return;

      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        // Step 1: Get presigned URL (same as Treasury Excel viewer)
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileId}/presigned-url`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to get presigned URL: ${response.status}`);
        }

        const presignedData = await response.json();
        
        if (!presignedData.url) {
          throw new Error('No presigned URL received');
        }

        // Step 2: Use presigned URL directly for iframe
        setPdfUrl(presignedData.url);
      } catch (err) {
        console.error('Error fetching PDF preview:', err);
        setError(err.message || 'Failed to load PDF preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPDFPreview();
  }, [open, fileId, tenantId]);

  const handleClose = () => {
    setPdfUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          PDF Preview
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && pdfUrl && (
          <Box sx={{ height: '100%', width: '100%' }}>
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="PDF Preview"
            />
          </Box>
        )}

        {!loading && !error && !pdfUrl && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No PDF preview available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFPreviewDialog;
